import { useState, useMemo, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import useAuth from "hooks/useAuth";
import getDecodedToken from "services/getDecodedToken";
import Avatar from "components/ui/Avatar";

const IconMenu = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);
const IconChevronsLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" />
  </svg>
);
const IconChevronsRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" />
  </svg>
);
const IconChevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const Sidebar = ({ routes = [], logo, brandText = "STOCK", brandAccent = "POS" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { auth } = useAuth() || {};
  const [rail, setRail] = useState(() => {
    try { return localStorage.getItem("sb_rail") === "1"; } catch (_) { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Push the current sidebar width to a CSS variable so .pm-main and .pm-topbar
  // shrink/expand together with the sidebar — kills the white-gap bug in rail mode.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--sidebar-width", rail ? "72px" : "260px");
    try { localStorage.setItem("sb_rail", rail ? "1" : "0"); } catch (_) {}
    return () => {
      // restore default when sidebar unmounts (e.g. on logout)
      root.style.removeProperty("--sidebar-width");
    };
  }, [rail]);

  const decoded = getDecodedToken() || {};
  const email = decoded.sub || auth?.user?.username || "";
  const username = decoded.username || (email ? email.split("@")[0] : "User");
  const role = decoded.role || decoded.authorities?.[0] || "Member";

  // Group routes by optional `section` (untouched if not present)
  const groups = useMemo(() => {
    const map = new Map();
    routes.forEach((r) => {
      const key = r.section || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries());
  }, [routes]);

  const brandPath = (logo && logo.innerLink) || "/home";

  return (
    <aside
      className={`pm-sidebar ${rail ? "pm-sidebar--rail" : ""} ${mobileOpen ? "pm-sidebar--open" : ""}`}
    >
      <div className="pm-sidebar__head">
        <a
          className="pm-sidebar__brand"
          href={brandPath}
          onClick={(e) => { e.preventDefault(); navigate(brandPath); }}
        >
          <span className="pm-sidebar__brand-mark">S</span>
          <span className="pm-sidebar__brand-text">
            {brandText}<em>{brandAccent}</em>
          </span>
        </a>

        <button
          type="button"
          className="pm-sidebar__mobile-toggle"
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          <IconMenu />
        </button>

        <button
          type="button"
          className="pm-sidebar__rail-toggle"
          aria-label={rail ? "Expand sidebar" : "Collapse sidebar"}
          onClick={() => setRail((v) => !v)}
        >
          {rail ? <IconChevronsRight /> : <IconChevronsLeft />}
        </button>
      </div>

      <nav className="pm-sidebar__scroll" aria-label="Primary">
        {groups.map(([section, items], gi) => (
          <div key={section || `g-${gi}`}>
            {section && <p className="pm-section-label">{section}</p>}
            <ul className="pm-nav">
              {items.map((r, i) => {
                const to = (r.layout || "") + r.path;
                return (
                  <li key={`${to}-${i}`}>
                    <NavLink
                      to={to}
                      end={r.end === true}
                      className={({ isActive }) =>
                        `pm-nav__item ${isActive ? "pm-nav__item--active" : ""}`
                      }
                      onClick={() => setMobileOpen(false)}
                      title={rail ? r.name : undefined}
                    >
                      <span className="pm-nav__icon">
                        {r.icon ? <i className={r.icon} /> : null}
                      </span>
                      <span className="pm-nav__label">{r.name}</span>
                      {r.badge && <span className="pm-nav__badge">{r.badge}</span>}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="pm-sidebar__foot">
        <div
          className="pm-user"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`${routes[0]?.layout || ""}/Profile`)}
          onKeyDown={(e) => { if (e.key === "Enter") navigate(`${routes[0]?.layout || ""}/Profile`); }}
        >
          <Avatar name={username} size="sm" />
          <div className="pm-user__meta">
            <div className="pm-user__name">{username}</div>
            <div className="pm-user__role">{String(role).replace(/_/g, " ")}</div>
          </div>
          <span className="pm-user__chev"><IconChevron /></span>
        </div>
      </div>
    </aside>
  );
};

Sidebar.propTypes = {
  routes: PropTypes.array,
  logo: PropTypes.shape({
    innerLink: PropTypes.string,
    outterLink: PropTypes.string,
    imgSrc: PropTypes.string,
    imgAlt: PropTypes.string,
  }),
  brandText: PropTypes.string,
  brandAccent: PropTypes.string,
};

export default Sidebar;
